// Card 61, the margin warning (R802, ui-map-interface.md §17.3.61).
//
// Mockup round thirty-six, card 02, "the warning in words". The engraver
// wrote a warning in the water nobody had crossed; the current chart
// prints a caution box. Both state one fact about the void past the world
// edge: it is not the game. The server clamps a course to the world
// rectangle (`sim/path.go`), so a move into the void stops at the edge, and
// the modern box says so. A new reader learns the rule from the chart and
// not from a hull that halts.
//
// The warning repeats along all four edges, one every `spacing_px`, with
// its centre `offset_px` outside the edge. It is set PARALLEL to its edge:
// the top and the bottom read left to right, the left edge reads from the
// bottom up and the right edge from the top down, the way a chart sets a
// word in its side margins.
//
// **It is STATIC, and it is the sheet.** What it draws is a function of the
// grid alone, so it packs on the hello, on a reading change and when the
// Fell face lands (the face's advances are measured at the bake). A replay
// film keeps it: the margin belongs to the sheet, as the isobaths do.
//
// **It culls nothing on the CPU.** A static pack is written at one view and
// kept through every pan, so a warning culled at the pack would be missing
// when a pan brings its edge in. The whole margin of the core world is
// twenty warnings of at most seventy glyphs, and the GPU clips the rest.

import { WORLD, WORLD_H } from '../../../world';
import { chart as S } from '../../../gen/strings';
import type { ChartFace } from '../../gfx/atlas';
import type { FrameInfo } from '../../layer';
import type { ChartContext, ChartSinks, Piece } from '../context';
import { stepPx } from '../steps';
import { CHART_INK, faceOf, inkOf } from './shared';

/** Which edge of the world a mark of the margin stands on. */
export type Edge = 'top' | 'right' | 'bottom' | 'left';

/** The four edges, in the order every margin piece walks them. */
export const EDGES: readonly Edge[] = ['top', 'right', 'bottom', 'left'];

/** One warning: its centre, in world px, and the way its text reads. */
export interface Warning {
  edge: Edge;
  x: number;
  y: number;
  /** The reading direction, a unit vector parallel to the edge. */
  ux: number;
  uy: number;
}

/**
 * Half the length of the straight path one line of text is laid along.
 * It only has to be longer than half the longest line.
 */
const PATH_HALF = 4096;

/**
 * Where the warnings of a world stand.
 *
 * An edge of length `len` holds `round(len / spacing)` warnings, one at
 * least, spread evenly and centred, so no warning stands in a corner.
 */
function warningsOf(
  w: number, h: number, offset: number, spacing: number,
): Warning[] {
  const along = (len: number): number[] => {
    const n = spacing > 0 ? Math.max(1, Math.round(len / spacing)) : 1;
    return Array.from({ length: n }, (_, k) => (len * (k + 0.5)) / n);
  };
  const out: Warning[] = [];
  for (const s of along(w)) out.push({ edge: 'top', x: s, y: -offset, ux: 1, uy: 0 });
  for (const s of along(h)) out.push({ edge: 'right', x: w + offset, y: s, ux: 0, uy: 1 });
  for (const s of along(w)) out.push({ edge: 'bottom', x: s, y: h + offset, ux: 1, uy: 0 });
  for (const s of along(h)) out.push({ edge: 'left', x: -offset, y: s, ux: 0, uy: -1 });
  return out;
}

export class MarginWarningPiece implements Piece {
  readonly id = 'margin_warning' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  readonly film = 'keep' as const;
  readonly rebuildOn = ['hello', 'reading', 'font'] as const;

  /** The warnings of this world. The tests read them. */
  warnings: Warning[] = [];

  rebuild(ctx: ChartContext): void {
    const f = ctx.cfg.pieces.margin_warning.fig;
    this.warnings = warningsOf(WORLD, WORLD_H, f.offset_px, f.spacing_px);
  }

  emit(sinks: ChartSinks, ctx: ChartContext, _frame: FrameInfo): void {
    const hist = ctx.reading === 'historical';
    for (const wn of this.warnings) {
      if (hist) latin(sinks, ctx, wn);
      else caution(sinks, ctx, wn);
    }
  }
}

/**
 * The historical warning: the Latin line, a thin curved rule under it and
 * the smaller second line. `a` runs along the edge and `b` across it, in
 * the warning's own frame.
 */
function latin(sinks: ChartSinks, ctx: ChartContext, wn: Warning): void {
  const r = ctx.cfg.pieces.margin_warning.historical;
  const f = r.fig;
  const ink = inkOf(r.ink, CHART_INK.pale);
  const face = faceOf('historical');
  const steps = ctx.cfg.pieces.margin_warning.text;
  text(sinks, ctx, wn, S.warning_title_historical, f.title_b, face,
    stepPx(face, steps.title), ink, r.alpha);
  // The rule rises `rule_sag` px toward the title at its middle.
  const n = Math.max(1, Math.round(f.rule_steps));
  let pa: number = -f.rule_half, pb: number = f.rule_b;
  for (let i = 1; i <= n; i++) {
    const u = i / n;
    const a = -f.rule_half + 2 * f.rule_half * u;
    const b = f.rule_b - f.rule_sag * 4 * u * (1 - u);
    seg(sinks, wn, pa, pb, a, b, r.width, ink, f.rule_alpha);
    pa = a;
    pb = b;
  }
  text(sinks, ctx, wn, S.warning_sub_historical, f.sub_b, face,
    stepPx(face, steps.sub), ink, f.sub_alpha);
}

/** The modern warning: the caution box, its head word, a rule, three lines. */
function caution(sinks: ChartSinks, ctx: ChartContext, wn: Warning): void {
  const r = ctx.cfg.pieces.margin_warning.modern;
  const f = r.fig;
  const ink = inkOf(r.ink, CHART_INK.pale);
  const face = faceOf('modern');
  const steps = ctx.cfg.pieces.margin_warning.text;
  const hw = f.box_w / 2, hh = f.box_h / 2;
  seg(sinks, wn, -hw, -hh, hw, -hh, r.width, ink, r.alpha);
  seg(sinks, wn, hw, -hh, hw, hh, r.width, ink, r.alpha);
  seg(sinks, wn, hw, hh, -hw, hh, r.width, ink, r.alpha);
  seg(sinks, wn, -hw, hh, -hw, -hh, r.width, ink, r.alpha);
  text(sinks, ctx, wn, S.warning_title_modern, -hh + f.caution_dy, face,
    stepPx(face, steps.caution), ink, f.caution_alpha);
  const rb = -hh + f.rule_dy;
  seg(sinks, wn, -hw + f.rule_inset, rb, hw - f.rule_inset, rb, f.rule_width, ink, f.rule_alpha);
  S.warning_lines_modern.forEach((line, k) => {
    text(sinks, ctx, wn, line, -hh + f.line_dy + k * f.line_pitch, face,
      stepPx(face, steps.line), ink, f.line_alpha);
  });
}

/** One segment between two points of the warning's own frame. */
function seg(
  sinks: ChartSinks, wn: Warning,
  a0: number, b0: number, a1: number, b1: number,
  width: number, ink: number, alpha: number,
): void {
  sinks.segments.line(
    wn.x + wn.ux * a0 - wn.uy * b0, wn.y + wn.uy * a0 + wn.ux * b0,
    wn.x + wn.ux * a1 - wn.uy * b1, wn.y + wn.uy * a1 + wn.ux * b1,
    width, ink, alpha,
  );
}

/**
 * One line of text, centred on the warning's axis at `b` across it.
 *
 * The run is laid along a straight path in the reading direction, so every
 * glyph turns with the edge. `upright` is off, because the direction of
 * each edge is chosen here and must not be flipped.
 *
 * The words are a part of a drawing with rules and a box, so they take the
 * size of their step and grow with the water as the drawing does: no
 * screen band holds them (R1796, §17.4).
 */
function text(
  sinks: ChartSinks, ctx: ChartContext, wn: Warning, str: string, b: number,
  face: ChartFace, size: number, ink: number, alpha: number,
): void {
  const x = wn.x - wn.uy * b, y = wn.y + wn.ux * b;
  const pts = [
    { x: x - wn.ux * PATH_HALF, y: y - wn.uy * PATH_HALF },
    { x: x + wn.ux * PATH_HALF, y: y + wn.uy * PATH_HALF },
  ];
  ctx.glyphs.along(sinks.quads, str, pts, face, size, ink, alpha, {
    anchor: 'centre', startS: PATH_HALF, upright: false,
  });
}

/** The registry's entry. */
export const marginWarning = new MarginWarningPiece();
